<template>
  <div class="new-inventory-assembly-container">
    <BrucdcumHeader :breadcrumb="['Inventory', 'New Inventory Assembly']" />
    <div class="new-inventory-assembly-header-row">
    </div>
    <form class="new-inventory-assembly-form">
      <div class="new-inventory-assembly-form-section new-inventory-assembly-card">
        <div class="new-inventory-assembly-form-section-header">
          <span class="new-inventory-assembly-header-emoji">🛠️</span> Assembly Details
        </div>
        <div class="new-inventory-assembly-form-columns new-inventory-assembly-form-row-flex">
          <!-- 4-column layout: Assembly Info, Assets Account, Purchase Info, Sales Info -->
          <div class="new-inventory-assembly-form-col new-inventory-assembly-stunning-form-col">
            <div class="new-inventory-assembly-form-section-header small">Assembly Info</div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--label-input">
              <LabelInputField
                input-id="nia-assembly-name"
                label="Inventory Assembly"
                v-model="assemblyName"
                placeholder="Inventory Assembly"
                flex
                required
                :has-error="!!assemblyNameError"
              >
                <div class="new-inventory-assembly-input-helper-text">E.g. Assembly A, Widget Set, Custom Kit</div>
                <div v-if="assemblyNameError" class="new-inventory-assembly-input-error-text">{{ assemblyNameError }}</div>
              </LabelInputField>
            </div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--label-dropdown">
              <LabelDropdown
                label="Category"
                select-id="nia-items-group"
                v-model="selectedItemGroup"
                flex
                wide
                searchable
                menu-class="new-inventory-assembly-dropdown-menu"
                :options="itemGroupDropdownOptions"
                @change="handleItemsGroupChange"
              >
                <div class="new-inventory-assembly-input-helper-text">Organize your assemblies by group</div>
              </LabelDropdown>
            </div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--label-date">
              <LabelDateField
                input-id="nia-as-of-date"
                label="As of"
                v-model="asOfDate"
                flex
              />
            </div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--checkbox">
              <LabelCheckboxField
                input-id="nia-active"
                label="Active"
                v-model="active"
                flex
              />
            </div>
          </div>
          <div class="new-inventory-assembly-form-col new-inventory-assembly-stunning-form-col">
            <div class="new-inventory-assembly-form-section-header small">Assets Account</div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--label-dropdown">
              <LabelDropdown
                label="Assets Account"
                select-id="nia-assets-account"
                v-model="selectedAssetsAccount"
                flex
                wide
                required
                searchable
                menu-class="new-inventory-assembly-dropdown-menu"
                :options="assetsAccountDropdownOptions"
                @change="handleAssetsAccountChange"
              />
            </div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--label-input">
              <LabelInputField
                input-id="nia-reorder-point"
                label="Reorder Point"
                v-model="reorderPoint"
                flex
              />
            </div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--label-input">
              <LabelInputField
                input-id="nia-on-hand-qty"
                label="On Hand Quantity"
                v-model="onHandQuantity"
                flex
              >
                <div class="new-inventory-assembly-input-helper-text">Items</div>
              </LabelInputField>
            </div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--label-input">
              <LabelInputField
                input-id="nia-total-value"
                label="Total(USD)"
                :model-value="totalValue"
                flex
                readonly
              />
            </div>
          </div>
          <div class="new-inventory-assembly-form-col new-inventory-assembly-stunning-form-col">
            <div class="new-inventory-assembly-form-section-header small">Purchase Information</div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--checkbox">
              <LabelCheckboxField
                input-id="nia-buy-product"
                label="I buy this product"
                v-model="buyProduct"
                flex
              />
            </div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--label-description">
              <LabelDescriptionField
                input-id="nia-purchase-description"
                label="Description on Purchase Transaction"
                v-model="purchaseDescription"
                :rows="2"
                flex
                :disabled="!buyProduct"
              />
            </div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--label-input">
              <LabelInputField
                input-id="nia-standard-cost"
                label="Standard Cost(USD)"
                v-model="standardCost"
                flex
                :disabled="!buyProduct"
                :has-error="!!standardCostError"
              >
                <div v-if="standardCostError" class="new-inventory-assembly-input-error-text">{{ standardCostError }}</div>
              </LabelInputField>
            </div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--label-dropdown">
              <LabelDropdown
                label="Cost of Good Sold"
                select-id="nia-cogs-account"
                v-model="selectedCostOfGoodsSold"
                flex
                wide
                required
                searchable
                menu-class="new-inventory-assembly-dropdown-menu"
                :options="costOfGoodsSoldDropdownOptions"
                @change="handleCostOfGoodsSoldChange"
              />
            </div>
          </div>
          <div class="new-inventory-assembly-form-col new-inventory-assembly-stunning-form-col">
            <div class="new-inventory-assembly-form-section-header small">Sales Information</div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--checkbox">
              <LabelCheckboxField
                input-id="nia-sell-product"
                label="I sell this product"
                v-model="sellProduct"
                flex
              />
            </div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--label-description">
              <LabelDescriptionField
                input-id="nia-sales-description"
                label="Description on Sales Transaction"
                v-model="salesDescription"
                :rows="2"
                flex
                :disabled="!sellProduct"
              />
            </div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--label-input">
              <LabelInputField
                input-id="nia-sales-price"
                label="Sales Price(USD)"
                v-model="salesPrice"
                placeholder="Sales Price(USD)"
                flex
                :disabled="!sellProduct"
                :has-error="!!salesPriceError"
                @input="validateSalesPriceAndStandardCost"
              >
                <div v-if="salesPriceError" class="new-inventory-assembly-input-error-text">{{ salesPriceError }}</div>
              </LabelInputField>
            </div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--label-dropdown">
              <LabelDropdown
                label="Income Account"
                select-id="nia-income-account"
                v-model="selectedIncomeAccount"
                flex
                wide
                required
                searchable
                :disabled="!sellProduct"
                menu-class="new-inventory-assembly-dropdown-menu"
                :options="incomeAccountDropdownOptions"
                @change="handleIncomeAccountChange"
              />
            </div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--checkbox">
              <LabelCheckboxField
                input-id="nia-taxable"
                label="Taxable"
                v-model="taxable"
                flex
              />
            </div>
            <div class="new-inventory-assembly-form-group new-inventory-assembly-form-group--checkbox">
              <LabelCheckboxField
                input-id="nia-commission-item"
                label="Commission item"
                v-model="commissionItem"
                flex
              />
            </div>
          </div>
        </div>
        <hr class="new-inventory-assembly-form-section-divider" />
        <div class="new-inventory-assembly-table-title">Inventory Assembly Item Table</div>
        <div class="new-inventory-assembly-table-responsive">
        <table class="new-inventory-assembly-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>On Hand Quantity</th>
              <th>Quantity Needed</th>
              <th>Average Cost(USD)</th>
              <th>Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in tableRows" :key="row.id">
              <td class="new-inventory-assembly-table-dropdown-cell">
                <LabelDropdown
                  hide-label
                  aria-label="Item"
                  :select-id="`nia-item-${row.id}`"
                  v-model="row.inventoryItemId"
                  flex
                  searchable
                  menu-class="new-inventory-assembly-dropdown-menu"
                  :options="itemDropdownOptions"
                  @change="handleItemSelect(row)"
                />
              </td>
              <td class="new-inventory-assembly-table-input-cell">
                <LabelInputField
                  hide-label
                  aria-label="Description"
                  :input-id="`nia-desc-${row.id}`"
                  v-model="row.description"
                  flex
                />
              </td>
              <td>{{ getOnHandQuantity(row.inventoryItemId) }}</td>
              <td class="new-inventory-assembly-table-input-cell new-inventory-assembly-table-input-cell--with-message">
                <LabelInputField
                  hide-label
                  aria-label="Quantity Needed"
                  :input-id="`nia-qty-${row.id}`"
                  :model-value="row.quantity"
                  type="number"
                  flex
                  :has-error="!!row.quantityNeededError"
                  @update:modelValue="onQuantityChange(row, $event)"
                >
                  <div v-if="row.quantityNeededError" class="new-inventory-assembly-qty-error">
                    Quantity Needed cannot exceed On Hand Quantity!
                  </div>
                </LabelInputField>
              </td>
              <td class="new-inventory-assembly-table-input-cell">
                <LabelInputField
                  hide-label
                  aria-label="Average Cost"
                  :input-id="`nia-avg-cost-${row.id}`"
                  v-model="row.avgCost"
                  type="number"
                  flex
                />
              </td>
              <td>{{ row.quantity && row.avgCost ? (row.quantity * row.avgCost).toFixed(2) : '' }}</td>
              <td>
                <button class="new-inventory-assembly-btn-delete" type="button" @click="removeRow(idx)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <path d="M7.5 8.5V14.5" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/>
                    <path d="M12.5 8.5V14.5" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/>
                    <rect x="4.5" y="5.5" width="11" height="11" rx="2" stroke="#fff" stroke-width="1.5"/>
                    <path d="M2.5 5.5H17.5" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/>
                    <path d="M8.5 2.5H11.5" stroke="#fff" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                </button>
              </td>
            </tr>
            <tr v-if="!tableRows.length">
              <td colspan="7" class="new-inventory-assembly-table-empty">No records to show</td>
            </tr>
          </tbody>
        </table>
        </div>
        <div class="new-inventory-assembly-total-value-row"><span class="new-inventory-assembly-total-label">Total value of materials cost:</span> <span>{{ totalValue }}</span></div>
        <div class="new-inventory-assembly-add-item-row">
          <PrimaryButton label="Add New Item" icon="bi bi-plus-lg" @click="addRow" />
        </div>
      </div>
      <div class="new-inventory-assembly-form-actions">
        <SecondaryButton label="Cancel" icon="bi bi-x-lg" @click="cancel" />
        <PrimaryButton label="Save and New" icon="bi bi-arrow-repeat" @click="saveAssembly('new')" />
        <PrimaryButton label="Save and Close" icon="bi bi-check-lg" @click="saveAssembly('close')" />
      </div>
    </form>
    <!-- Custom Modal for New Category -->
    <AddNewItemGroup
      :visible="showItemsGroupModal"
      :companyId="$route.params.id"
      item-type="Inventory"
      @ok="onAddItemGroupOk"
      @cancel="closeItemsGroupModal"
    />
    <div v-if="showToast" class="new-inventory-assembly-custom-toast">{{ toastMessage }}</div>
    <div v-if="showModal" class="new-inventory-assembly-custom-modal-overlay">
      <div class="new-inventory-assembly-custom-modal">
        <div class="new-inventory-assembly-custom-modal-header">
          <span>Error</span>
          <button class="new-inventory-assembly-modal-close" @click="closeModalAlert">&times;</button>
        </div>
        <div class="new-inventory-assembly-custom-modal-body">
          <span class="new-inventory-assembly-modal-message">{{ modalMessage }}</span>
        </div>
        <div class="new-inventory-assembly-custom-modal-footer">
          <PrimaryButton label="OK" icon="bi bi-check-lg" @click="closeModalAlert" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '../styles/NewInventoryAssembly.css';
import '../styles/inventory-label-field-overrides.css';
import ApiService from '@/services/api.service';
import CompanyService from '@/services/company.service.js';
import AddNewItemGroup from '@/modules/company/views/AddNewItemGroup.vue';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import LabelDescriptionField from '@/modules/consultant/components/LabelDescriptionField.vue';
import LabelCheckboxField from '@/modules/consultant/components/LabelCheckboxField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'NewInventoryAssembly',
  components: {
    AddNewItemGroup,
    BrucdcumHeader,
    LabelDropdown,
    LabelInputField,
    LabelDateField,
    LabelDescriptionField,
    LabelCheckboxField,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return {
      itemGroups: [],
      selectedItemGroup: '',
      showItemsGroupModal: false,
      newItemsGroupName: '',
      previousItemGroup: '',
      sellProduct: true,
      selectedAssetsAccount: '',
      assetsAccounts: [],
      reorderPoint: '',
      onHandQuantity: '',
      asOfDate: `${mm}/${dd}/${yyyy}`,
      standardCost: '',
      selectedIncomeAccount: '',
      incomeAccounts: [],
      selectedCostOfGoodsSold: '',
      costOfGoodsSoldAccounts: [],
      tableRows: [],
      nextRowId: 1,
      buyProduct: true,
      inventoryItems: [],
      reorderPointErrorRows: [],
      adjustedItemIds: [],
      showToast: false,
      toastMessage: '',
      showModal: false,
      modalMessage: '',
      salesPriceError: '',
      standardCostError: '',
      total: '',
      salesDescription: '',
      purchaseDescription: '',
      salesPrice: '',
      assemblyNameError: '',
      assemblyName: '',
      existingAssemblies: [],
      active: true,
      taxable: true,
      commissionItem: false,
    };
  },
  computed: {
    totalValue() {
      return this.tableRows.reduce((sum, row) => sum + (row.quantity && row.avgCost ? row.quantity * row.avgCost : 0), 0).toFixed(2);
    },
    itemGroupDropdownOptions() {
      return [
        { value: '', label: 'Select Category' },
        ...this.itemGroups.map((group) => ({
          value: group.id,
          label: group.name,
        })),
        { value: '__add_new__', label: 'Add New Category' },
      ];
    },
    assetsAccountDropdownOptions() {
      return this.buildAccountDropdownOptions(this.assetsAccounts, 'Select Assets Account');
    },
    incomeAccountDropdownOptions() {
      return this.buildAccountDropdownOptions(this.incomeAccounts, 'Select Income Account');
    },
    costOfGoodsSoldDropdownOptions() {
      return this.buildAccountDropdownOptions(this.costOfGoodsSoldAccounts, 'Select Cost of Goods Sold Account');
    },
    itemDropdownOptions() {
      return [
        { value: '', label: 'Select Item' },
        ...this.inventoryItems
          .filter((item) => Number(item.onHandQuantity) > 0)
          .map((item) => ({
            value: item.id,
            label: item.name,
          })),
        { value: '__add_new__', label: 'Create New Item…' },
      ];
    },
  },
  watch: {
    onHandQuantity() {
      this.updateTotal();
    },
    standardCost() {
      this.updateTotal();
    }
  },
  mounted() {
    this.fetchInventoryItems();
    this.fetchAdjustedItems();
    this.fetchAssemblies();
    this.fetchCompanyAccounts();
    this.fetchItemGroups();
  },
  methods: {
    async fetchCompanyAccounts() {
      const companyId = this.$route.params.id;
      try {
        const res = await ApiService.get(`/company-account/company/${companyId}`);
        const accounts = res.data.accounts;
        this.incomeAccounts = accounts.filter(a => a.accountType === 'Income' || a.accountType === 'Other Income');
        this.assetsAccounts = accounts.filter(a => a.accountType === 'Inventory Asset' || a.accountType === 'Fixed Asset');
        this.costOfGoodsSoldAccounts = accounts.filter(a => a.accountType === 'Cost of Goods Sold');
      } catch (error) {
        this.incomeAccounts = [];
        this.assetsAccounts = [];
        this.costOfGoodsSoldAccounts = [];
      }
    },
    async fetchInventoryItems() {
      const companyId = this.$route.params.id;
      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/inventory/companies/${companyId}/items`);
        if (!response.ok) throw new Error('Failed to fetch inventory items');
        const items = await response.json();
        this.inventoryItems = items;
      } catch (e) {
        this.inventoryItems = [];
      }
    },
    async fetchAdjustedItems() {
      const companyId = this.$route.params.id;
      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/inventory/companies/${companyId}/stock-adjustment-items`);
        if (!response.ok) throw new Error('Failed to fetch stock adjustment items');
        const items = await response.json();
        this.adjustedItemIds = items.map(item => item.inventoryItemId);
      } catch (e) {
        this.adjustedItemIds = [];
      }
    },
    async fetchAssemblies() {
      const companyId = this.$route.params.id;
      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/inventory/companies/${companyId}/assemblies`);
        if (!response.ok) throw new Error('Failed to fetch assemblies');
        const assemblies = await response.json();
        this.existingAssemblies = assemblies;
      } catch (e) {
        this.existingAssemblies = [];
      }
    },
    async fetchItemGroups() {
      try {
        const companyId = this.$route.params.id;
        const groups = await CompanyService.getAllItemGroups(companyId, 'Inventory');
        this.itemGroups = Array.isArray(groups) ? groups : [];
      } catch (err) {
        this.itemGroups = [];
      }
    },
    showToastMessage(msg) {
      this.toastMessage = msg;
      this.showToast = true;
      setTimeout(() => { this.showToast = false; }, 2000);
    },
    buildAccountDropdownOptions(accounts, placeholder) {
      return [
        { value: '', label: placeholder },
        { value: '__add_new__', label: 'Add New Account' },
        ...accounts.map((account) => ({
          value: account.id,
          label: `${account.accountNumber} - ${account.accountName} - ${account.accountType}`,
        })),
      ];
    },
    onQuantityChange(row, value) {
      row.quantity = value === '' ? '' : Number(value);
      this.validateQuantityNeeded(row);
    },
    handleItemSelect(row) {
      if (String(row.inventoryItemId) === '__add_new__') {
        this.$router.push(`/company/${this.$route.params.id}/dashboard/inventory/new-item`);
        row.inventoryItemId = '';
        return;
      }
      if (!row.inventoryItemId) return;

      const selected = this.inventoryItems.find(
        (item) => String(item.id) === String(row.inventoryItemId)
      );
      if (selected) {
        row.name = selected.name;
        row.description = selected.descriptionSales || selected.descriptionPurchase || '';
        row.avgCost =
          selected.standardCost != null
            ? Number(selected.standardCost)
            : (selected.purchasePrice != null ? Number(selected.purchasePrice) : 0);
        this.validateQuantityNeeded(row);
      }
    },
    handleItemsGroupChange() {
      if (String(this.selectedItemGroup) === '__add_new__') {
        this.showItemsGroupModal = true;
        this.$nextTick(() => {
          this.selectedItemGroup = this.previousItemGroup;
        });
      } else {
        this.previousItemGroup = this.selectedItemGroup;
      }
    },
    async onAddItemGroupOk(group) {
      this.showItemsGroupModal = false;
      await this.fetchItemGroups();
      if (group && group.id) {
        this.selectedItemGroup = group.id;
        this.previousItemGroup = group.id;
        this.showToastMessage && this.showToastMessage('Item group created!');
      }
    },
    closeItemsGroupModal() {
      this.showItemsGroupModal = false;
    },
    handleAssetsAccountChange() {
      if (String(this.selectedAssetsAccount) === '__add_new__') {
        this.$router.push(`/company/${this.$route.params.id}/dashboard/new-account`);
        this.selectedAssetsAccount = '';
      }
    },
    handleIncomeAccountChange() {
      if (String(this.selectedIncomeAccount) === '__add_new__') {
        this.$router.push(`/company/${this.$route.params.id}/dashboard/new-account`);
        this.selectedIncomeAccount = '';
      }
    },
    handleCostOfGoodsSoldChange() {
      if (String(this.selectedCostOfGoodsSold) === '__add_new__') {
        this.$router.push(`/company/${this.$route.params.id}/dashboard/new-account`);
        this.selectedCostOfGoodsSold = '';
      }
    },
    addRow() {
      this.tableRows.push({ id: this.nextRowId++, inventoryItemId: '', name: '', description: '', quantity: 0, avgCost: 0 });
    },
    removeRow(idx) {
      this.tableRows.splice(idx, 1);
    },
    getOnHandQuantity(itemId) {
      const item = this.inventoryItems.find((i) => String(i.id) === String(itemId));
      return item ? Number(item.onHandQuantity) || 0 : 0;
    },
    validateQuantityNeeded(row) {
      const onHand = this.getOnHandQuantity(row.inventoryItemId);
      let needed = Number(row.quantity) || 0;
      if (needed > onHand) {
        row.quantity = onHand;
        row.quantityNeededError = true;
      } else {
        row.quantityNeededError = false;
      }
    },
    validateSalesPriceAndStandardCost() {
      const salesPrice = Number(this.salesPrice || 0);
      const standardCost = Number(this.standardCost || 0);
      const totalMaterialsCost = Number(this.totalValue);
      if (!isNaN(salesPrice) && !isNaN(standardCost) && salesPrice < standardCost) {
        this.salesPriceError = 'Sales price cannot be below standard cost.';
      } else if (!isNaN(salesPrice) && !isNaN(totalMaterialsCost) && salesPrice < totalMaterialsCost) {
        this.salesPriceError = 'Sales price cannot be less than total value of materials cost.';
      } else {
        this.salesPriceError = '';
      }
    },
    async saveAssembly(action) {
      this.assemblyNameError = '';
      const trimmedName = this.assemblyName ? this.assemblyName.trim() : '';
      if (!trimmedName) {
        this.assemblyNameError = 'Please enter a valid assembly name.';
        return;
      }
      // Check for duplicate assembly name (case-insensitive, trimmed)
      const duplicate = this.existingAssemblies.some(a => a.name && a.name.trim().toLowerCase() === trimmedName.toLowerCase());
      if (duplicate) {
        this.assemblyNameError = 'An inventory assembly with this name already exists (case-insensitive).';
        return;
      }
      this.validateSalesPriceAndStandardCost();
      if (this.salesPriceError) {
        this.showModalAlert(this.salesPriceError);
        return;
      }
      const companyId = this.$route.params.id;
      // Filter out rows with invalid inventoryItemId
      const validRows = this.tableRows.filter(
        row => row.inventoryItemId && row.inventoryItemId !== '__add_new__'
      );

      if (validRows.length === 0) {
        alert('Please select at least one valid item for the assembly.');
        return;
      }

      const toNullableNumber = (value) => {
        if (value === '' || value === null || value === undefined) return null;
        const num = Number(value);
        return Number.isFinite(num) ? num : null;
      };

      const payload = {
        name: trimmedName,
        description: '',
        salesDescription: this.salesDescription || '',
        salesPrice: toNullableNumber(this.salesPrice) ?? 0,
        incomeAccount: this.selectedIncomeAccount || null,
        purchaseDescription: this.purchaseDescription || '',
        standardCost: toNullableNumber(this.standardCost),
        costOfGoodsSoldAccount: this.selectedCostOfGoodsSold || null,
        assetsAccount: this.selectedAssetsAccount || null,
        reorderPoint: toNullableNumber(this.reorderPoint),
        onHandQuantity: toNullableNumber(this.onHandQuantity) ?? 0,
        asOfDate: this.asOfDate || null,
        itemGroup: this.selectedItemGroup || null,
        active: this.active,
        commissionItem: this.commissionItem,
        sellProduct: this.sellProduct,
        buyProduct: this.buyProduct,
        taxable: this.taxable,
        total: toNullableNumber(this.totalValue),
        items: validRows.map(row => ({
          inventoryItemId: row.inventoryItemId,
          name: row.name,
          description: row.description || '',
          quantity: toNullableNumber(row.quantity) ?? 0,
          avgCost: toNullableNumber(row.avgCost) ?? 0
        })),
      };
      let hasReorderError = false;
      this.reorderPointErrorRows = [];
      let hasQtyError = false;
      let modalErrorMessage = '';
      for (const row of this.tableRows) {
        const item = this.inventoryItems.find(i => i.id === row.inventoryItemId);
        if (item && item.reorderPoint != null && item.onHandQuantity != null) {
          if ((item.onHandQuantity - row.quantity) < item.reorderPoint) {
            hasReorderError = true;
            this.reorderPointErrorRows.push(row.id);
          }
        }
        const onHand = this.getOnHandQuantity(row.inventoryItemId);
        const needed = Number(row.quantity) || 0;
        if (needed > onHand) {
          row.quantityNeededError = true;
          hasQtyError = true;
        } else {
          row.quantityNeededError = false;
        }
      }
      if (hasReorderError || hasQtyError) {
        if (hasReorderError) {
          modalErrorMessage = 'One or more items would fall below their reorder point. Please adjust quantities.';
        }
        if (hasQtyError) {
          if (modalErrorMessage) modalErrorMessage += '\n';
          modalErrorMessage += 'One or more Quantity Needed values exceed On Hand Quantity.';
        }
        this.showModalAlert(modalErrorMessage);
        return;
      }
      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/inventory/companies/${companyId}/assemblies`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          let serverMessage = 'Failed to save assembly';
          try {
            const errorBody = await response.json();
            if (errorBody?.error) serverMessage = errorBody.error;
          } catch (_) { /* ignore parse errors */ }
          throw new Error(serverMessage);
        }
        if (action === 'close') {
          this.$router.push({ name: 'BuildAssembly', params: { id: companyId } });
        } else {
          this.resetForm();
        }
      } catch (error) {
        alert(error.message || 'Failed to save assembly');
      }
    },
    resetForm() {
      this.sellProduct = true;
      this.selectedAssetsAccount = '';
      this.reorderPoint = '';
      this.onHandQuantity = '';
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      this.asOfDate = `${mm}/${dd}/${yyyy}`;
      this.standardCost = '';
      this.selectedIncomeAccount = '';
      this.selectedCostOfGoodsSold = '';
      this.selectedItemGroup = '';
      this.buyProduct = true;
      this.tableRows = [];
      this.nextRowId = 1;
      this.assemblyName = '';
      this.salesDescription = '';
      this.purchaseDescription = '';
      this.salesPrice = '';
      this.active = true;
      this.taxable = true;
      this.commissionItem = false;
    },
    showModalAlert(msg) {
      this.modalMessage = msg;
      this.showModal = true;
    },
    closeModalAlert() {
      this.showModal = false;
      this.modalMessage = '';
    },
    cancel() {
      this.$router.back();
    },
    updateTotal() {
      const qty = parseFloat(this.onHandQuantity);
      const cost = parseFloat(this.standardCost);
      if (!isNaN(qty) && !isNaN(cost)) {
        this.total = (qty * cost).toFixed(2);
      } else {
        this.total = '';
      }
    },
    addNewAssembly() {
      // Existing logic for adding new assembly
    },
    onSearchClick() {
      // Placeholder for search logic
      alert('Search functionality coming soon!');
    }
  }
};
</script> 